/**
 * Coss Navigation — coss-nav.js
 * Handles:
 *   1. Desktop categories — CSS hover (mega-drop is full-width absolute)
 *   2. Desktop brands — click toggle, fixed centered 70vw dropdown
 *      search filter, alphabet scroll, sync active letter on scroll
 *   3. Mobile drawer — open/close, section accordion, brands search, alpha strip
 *   4. Header height measurement → sets --cs-header-h for brands dropdown position
 */
(function () {
  'use strict';

  const $ = (sel, ctx) => (ctx || document).querySelector(sel);
  const $$ = (sel, ctx) => Array.from((ctx || document).querySelectorAll(sel));

  function debounce(fn, ms) {
    let t;
    return (...a) => { clearTimeout(t); t = setTimeout(() => fn(...a), ms); };
  }

  /* ═══════════════════════════════════════════════════
     1. MEASURE HEADER HEIGHT → --cs-header-h
        Brands dropdown uses position: fixed, so it
        needs to know where the header ends.
        Also accounts for xo-sticky fixed/sticky header
        that may shift cs-nav up in the flow.
     ═══════════════════════════════════════════════════ */
  function getHeaderBottom() {
    const nav = document.getElementById('cs-nav');
    let bottom = 0;
    if (nav) bottom = Math.max(bottom, nav.getBoundingClientRect().bottom);
    // xo-sticky header may be position:fixed (out of flow) so measure it too
    const stickyEl = document.querySelector('xo-sticky');
    if (stickyEl) bottom = Math.max(bottom, stickyEl.getBoundingClientRect().bottom);
    return Math.max(bottom, 0);
  }

  function measureHeader() {
    function update() {
      document.documentElement.style.setProperty('--cs-header-h', getHeaderBottom() + 'px');
    }

    update();
    window.addEventListener('resize', debounce(update, 100));
    window.addEventListener('scroll', debounce(update, 50), { passive: true });

    // Re-measure after sticky header settles
    setTimeout(update, 400);
    setTimeout(update, 1000);
  }

  /* ═══════════════════════════════════════════════════
     2. DESKTOP CATEGORIES DROPDOWN  (hover with delay)
        Pure CSS :hover closes the fixed panel the instant
        the cursor leaves the nav bar — before it can reach
        the dropdown.  We mirror the Brands pattern: start a
        120 ms close timer on mouseleave, cancel it on
        mouseenter of either the trigger or the panel.
     ═══════════════════════════════════════════════════ */
  function initDesktopCategories() {
    $$('.cs-cat-item--drop').forEach(function (item) {
      var drop  = item.querySelector('.cs-cat-megadrop');
      if (!drop) return;
      var timer = null;

      function show() {
        clearTimeout(timer);
        // Recompute position fresh each time so sticky transitions don't cause stale values
        if (drop) drop.style.top = getHeaderBottom() + 'px';
        item.classList.add('cs-cat-item--open');
      }
      function hide() {
        timer = setTimeout(function () {
          item.classList.remove('cs-cat-item--open');
        }, 120);
      }

      item.addEventListener('mouseenter', show);
      item.addEventListener('mouseleave', hide);
      drop.addEventListener('mouseenter', function () { clearTimeout(timer); });
      drop.addEventListener('mouseleave', hide);
    });
  }

  /* ═══════════════════════════════════════════════════
     3. DESKTOP BRANDS DROPDOWN  (JS click/hover toggle)
     ═══════════════════════════════════════════════════ */
  function initBrands() {
    const btn      = document.getElementById('cs-brands-btn');
    const drop     = document.getElementById('cs-brands-drop');
    const search   = document.getElementById('cs-brands-search');
    const clearBtn = document.getElementById('cs-brands-clear');
    const scroll   = document.getElementById('cs-brands-scroll');
    const alphaWrap = $('.cs-alpha-strip');
    const wrap     = document.getElementById('cs-brands-wrap');

    if (!btn || !drop) return;

    let closeTimer = null;

    function positionDrop() {
      const rect = btn.getBoundingClientRect();
      drop.style.top = rect.bottom + 'px';
    }

    function openDrop() {
      clearTimeout(closeTimer);
      if (drop.hasAttribute('hidden')) {
        positionDrop();
        drop.removeAttribute('hidden');
        btn.setAttribute('aria-expanded', 'true');
      }
    }

    function closeDrop() {
      closeTimer = setTimeout(() => {
        drop.setAttribute('hidden', '');
        btn.setAttribute('aria-expanded', 'false');
      }, 150);
    }

    btn.addEventListener('mouseenter', openDrop);
    btn.addEventListener('mouseleave', closeDrop);
    drop.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    drop.addEventListener('mouseleave', closeDrop);

    /* Escape key */
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && !drop.hasAttribute('hidden')) {
        drop.setAttribute('hidden', '');
        btn.setAttribute('aria-expanded', 'false');
        btn.focus();
      }
    });

    /* Alpha click → scroll */
    if (alphaWrap && scroll) {
      $$('.cs-alpha-btn', alphaWrap).forEach(ab => {
        ab.addEventListener('click', e => {
          e.preventDefault();
          const target = document.getElementById(ab.dataset.target);
          if (target) {
            scroll.scrollTo({ top: target.offsetTop - scroll.offsetTop, behavior: 'smooth' });
          }
          setActiveAlpha(alphaWrap, '.cs-alpha-btn', ab.dataset.letter);
        });
      });

      /* Sync alpha on manual scroll */
      scroll.addEventListener('scroll', debounce(() => {
        let current = null;
        $$('.cs-brand-group', scroll).forEach(g => {
          if (!g.classList.contains('cs-hidden') &&
              g.offsetTop - scroll.offsetTop <= scroll.scrollTop + 24) {
            current = g.dataset.letter;
          }
        });
        if (current) setActiveAlpha(alphaWrap, '.cs-alpha-btn', current);
      }, 80));
    }

    /* Brand search */
    if (search) {
      search.addEventListener('input', () => {
        const q = search.value.trim();
        clearBtn && clearBtn.toggleAttribute('hidden', !q);
        filterBrands(q, '.cs-brand-group', '.cs-brand-link', 'cs-brands-noresult');
      });

      clearBtn && clearBtn.addEventListener('click', () => {
        search.value = '';
        clearBtn.setAttribute('hidden', '');
        filterBrands('', '.cs-brand-group', '.cs-brand-link', 'cs-brands-noresult');
        search.focus();
      });
    }
  }

  /* ═══════════════════════════════════════════════════
     3. SHARED: filter brand list
     ═══════════════════════════════════════════════════ */
  function filterBrands(query, groupSel, itemSel, noResId) {
    const q      = query.toLowerCase();
    const noRes  = document.getElementById(noResId);
    let anyVis   = false;

    $$(groupSel).forEach(group => {
      let grpVis = false;
      $$(itemSel, group).forEach(item => {
        const match = !q || item.textContent.trim().toLowerCase().includes(q);
        item.classList.toggle('cs-hidden', !match);
        if (match) grpVis = true;
      });
      group.classList.toggle('cs-hidden', !grpVis);
      if (grpVis) anyVis = true;
    });

    noRes && noRes.toggleAttribute('hidden', anyVis);
  }

  function setActiveAlpha(strip, btnSel, letter) {
    $$(btnSel, strip).forEach(b => b.classList.toggle('is-active', b.dataset.letter === letter));
  }

  /* ═══════════════════════════════════════════════════
     4. MOBILE DRAWER  open / close
     ═══════════════════════════════════════════════════ */
  function initMobileDrawer() {
    const hamburger = document.getElementById('cs-hamburger');
    const mobile    = document.getElementById('cs-mobile');
    const overlay   = document.getElementById('cs-mobile-overlay');
    const closeBtn  = document.getElementById('cs-mobile-close');

    if (!hamburger || !mobile) return;

    function open() {
      mobile.classList.add('cs-is-open');
      mobile.setAttribute('aria-hidden', 'false');
      hamburger.setAttribute('aria-expanded', 'true');
      csScrollLock.lock('nav');
      setTimeout(() => closeBtn && closeBtn.focus(), 60);
    }

    function close() {
      mobile.classList.add('cs-is-closing');
      hamburger.setAttribute('aria-expanded', 'false');
      setTimeout(() => {
        mobile.classList.remove('cs-is-open', 'cs-is-closing');
        mobile.setAttribute('aria-hidden', 'true');
        csScrollLock.unlock('nav');
      }, 280);
    }

    hamburger.addEventListener('click', open);
    closeBtn && closeBtn.addEventListener('click', close);
    overlay && overlay.addEventListener('click', close);
    mobile.addEventListener('keydown', e => { if (e.key === 'Escape') close(); });
  }

  /* ═══════════════════════════════════════════════════
     5. MOBILE TOP-LEVEL SECTIONS  (Brands / Categories)
     ═══════════════════════════════════════════════════ */
  function initMobileSections() {
    $$('.cs-mob-trigger').forEach(trigger => {
      const panel = trigger.nextElementSibling;
      if (!panel) return;

      function toggle(e) {
        if (e.type === 'keydown' && e.key !== 'Enter' && e.key !== ' ') return;
        if (e.type === 'keydown') e.preventDefault();

        const open = trigger.getAttribute('aria-expanded') === 'true';

        /* Collapse all sections first */
        $$('.cs-mob-trigger').forEach(t => {
          t.setAttribute('aria-expanded', 'false');
          t.nextElementSibling && t.nextElementSibling.setAttribute('hidden', '');
        });

        if (!open) {
          trigger.setAttribute('aria-expanded', 'true');
          panel.removeAttribute('hidden');
        }
      }

      trigger.addEventListener('click', toggle);
      trigger.addEventListener('keydown', toggle);
    });
  }

  /* ═══════════════════════════════════════════════════
     6. MOBILE CATEGORIES ACCORDION
     ═══════════════════════════════════════════════════ */
  function initMobileCategories() {
    /* L1 toggles */
    $$('.cs-mob-l1__toggle').forEach(btn => {
      const wrap = btn.closest('.cs-mob-l1__row') && btn.closest('.cs-mob-l1__row').nextElementSibling;
      if (!wrap) return;
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        wrap.toggleAttribute('hidden', open);
      });
    });

    /* L2 toggles */
    $$('.cs-mob-l2__toggle').forEach(btn => {
      const wrap = btn.closest('.cs-mob-l2__row') && btn.closest('.cs-mob-l2__row').nextElementSibling;
      if (!wrap) return;
      btn.addEventListener('click', () => {
        const open = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', String(!open));
        wrap.toggleAttribute('hidden', open);
      });
    });
  }

  /* ═══════════════════════════════════════════════════
     7. MOBILE BRANDS SEARCH
     ═══════════════════════════════════════════════════ */
  function initMobileBrandsSearch() {
    const search = document.getElementById('cs-mob-brands-search');
    if (!search) return;

    search.addEventListener('input', () => {
      filterBrands(search.value.trim(), '.cs-mob-brand-grp', '.cs-mob-brand-item', 'cs-mob-brands-nores');
    });
  }

  /* ═══════════════════════════════════════════════════
     8. MOBILE ALPHA STRIP
     ═══════════════════════════════════════════════════ */
  function initMobileAlpha() {
    $$('.cs-mob-alpha').forEach(alphaNav => {
      $$('.cs-mob-alpha-btn', alphaNav).forEach(btn => {
        btn.addEventListener('click', () => {
          const listEl  = document.getElementById(btn.dataset.list);
          const target  = document.getElementById(btn.dataset.target);
          if (listEl && target) {
            listEl.scrollTo({ top: target.offsetTop - listEl.offsetTop, behavior: 'smooth' });
          }
          setActiveAlpha(alphaNav, '.cs-mob-alpha-btn', btn.dataset.letter);
        });
      });

      /* Sync on scroll */
      const listId = $('[data-list]', alphaNav)?.dataset.list;
      const list   = listId ? document.getElementById(listId) : null;
      if (list) {
        list.addEventListener('scroll', debounce(() => {
          let current = null;
          $$('.cs-mob-brand-grp', list).forEach(g => {
            if (!g.classList.contains('cs-hidden') &&
                g.offsetTop - list.offsetTop <= list.scrollTop + 16) {
              current = g.dataset.letter;
            }
          });
          if (current) setActiveAlpha(alphaNav, '.cs-mob-alpha-btn', current);
        }, 80));
      }
    });
  }

  /* ═══════════════════════════════════════════════════
     9. CURRENCY PATCH — "Rs." → "₹" everywhere,
        including dynamic XO price renders
     ═══════════════════════════════════════════════════ */
  function patchCurrencyText(root) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    let node;
    while ((node = walker.nextNode())) {
      let v = node.nodeValue;
      if (v.indexOf('Rs.') !== -1) v = v.replace(/Rs\.\s*/g, '₹');
      v = v.replace(/(₹[\d,]+)\.00\b/g, '$1');
      if (v !== node.nodeValue) node.nodeValue = v;
    }
  }

  function initCurrencyPatch() {
    patchCurrencyText(document.body);
    new MutationObserver(mutations => {
      for (const m of mutations) {
        for (const n of m.addedNodes) {
          if (n.nodeType === 1) patchCurrencyText(n);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  /* ═══════════════════════════════════════════════════
     INIT
     ═══════════════════════════════════════════════════ */
  function init() {
    measureHeader();
    initDesktopCategories();
    initBrands();
    initMobileDrawer();
    initMobileSections();
    initMobileCategories();
    initMobileBrandsSearch();
    initMobileAlpha();
    initCurrencyPatch();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
